import { useTimeSaved } from '../hooks/useTimeSaved.js'
import { formatHoursSaved, formatMoney, timeAgo } from '../lib/format.js'
import KitchenLoader from './KitchenLoader.jsx'

// TimeSavedCard — "hours back since you joined".
//
// A read-only render of the time-saved ledger (savings_events rows where
// source='time_saved', logged by the approve-and-execute reorder
// executor). Headline = hours saved; dollars saved is a clearly SECONDARY
// line — never the headline. No new dollar math: the dollars total is
// passed in (sourced from the existing vw_savings_summary via
// useHuddleData.savingsSummary), so this card and LifetimeSavings never
// disagree.
//
// Props:
//   dollarsSaved — the existing lifetime dollars figure (number). Rendered
//                  as the secondary line only. Optional; omitted/0 hides it.
//
// House rules honored:
//   - All hooks run BEFORE any conditional return (React #310 — see the
//     PromotionsScreen incident in CLAUDE.md).
//   - KitchenLoader for the loading state, never a blue spinner.
//   - Sentence case, no emoji, no AI-cost/model/cents language.
export default function TimeSavedCard({ dollarsSaved = 0 }) {
  // Hook FIRST — before any early return.
  const { totalMinutes, asOf, eventCount, loading, error, dollarsMatchScope } =
    useTimeSaved()

  const sectionClass =
    'mx-4 mt-4 rounded-2xl border border-emerald-200 bg-white p-5 shadow-sm'

  // Loading first paint (no data yet) — kitchen loader, never a spinner.
  if (loading && eventCount === 0) {
    return (
      <section className={sectionClass} aria-label="Time saved">
        <div className="text-xs font-medium text-emerald-700">Time saved</div>
        <div className="mt-2">
          <KitchenLoader
            align="row"
            size="sm"
            messages={['Tallying the hours we’ve handed back…']}
          />
        </div>
      </section>
    )
  }

  // Error fallback — the fetch FAILED. This MUST take precedence over the
  // eventCount===0 empty state: an operator who actually has logged hours
  // would otherwise be told "nothing yet" (the hook zeroes counts on error),
  // which misrepresents a failure as a true-empty success. Deterministic,
  // customer-safe copy — sentence case, no emoji, no raw error string, no
  // AI-cost/model language. (CLAUDE.md: failed features show a distinct
  // deterministic fallback, never a misleading answer.)
  if (error) {
    return (
      <section className={sectionClass} aria-label="Time saved">
        <div className="text-xs font-medium text-emerald-700">Time saved</div>
        <div className="mt-1 text-sm text-black/70">
          We couldn’t load your time saved right now — check back shortly.
        </div>
      </section>
    )
  }

  // Empty state — no approved actions yet. Friendly nudge, NOT "$0" /
  // "0 hours" / a raw error. (Reached only when there is NO error — the
  // error branch above guards against an error masquerading as empty.)
  if (eventCount === 0) {
    return (
      <section className={sectionClass} aria-label="Time saved">
        <div className="text-xs font-medium text-emerald-700">Time saved</div>
        <div className="mt-1 text-sm text-black/70">
          Nothing yet — your first approved action starts the clock.
        </div>
      </section>
    )
  }

  const headline = formatHoursSaved(totalMinutes)
  const dollars = Number(dollarsSaved) || 0
  const asOfAgo = timeAgo(asOf)

  return (
    <section className={sectionClass} aria-label="Time saved">
      <div className="text-xs font-medium text-emerald-700">
        Hours back since you joined
      </div>

      {/* Headline = hours. The dollars line is rendered SEPARATELY below,
          never inside this headline node. */}
      <div className="mt-1 flex items-baseline gap-3">
        <span className="text-4xl font-medium tabular-nums text-emerald-700">
          {headline}
        </span>
      </div>

      {/* Secondary line — dollars saved. Visually subordinate (small,
          muted) and physically AFTER the headline in the DOM. Rendered ONLY
          when the dollars figure is scope-compatible with the hours headline
          (dollarsMatchScope): the group-wide vw_savings_summary total is
          apples-to-apples with group-roll-up or single-unit hours, but NOT
          with one selected unit's hours inside a grouped account — so it's
          hidden there to avoid implying those hours are worth the group's $. */}
      {dollarsMatchScope && dollars > 0 && (
        <div className="mt-1 text-sm text-black/60">
          <span className="tabular-nums">{formatMoney(dollars)}</span> saved
        </div>
      )}

      {asOfAgo && (
        <div className="mt-2 text-xs text-black/55">
          as of {asOfAgo}
        </div>
      )}
    </section>
  )
}
